/**
 * MiniMe pace logic — shared by the photographer app (pill color + alert
 * banner + edit-page dot) and the admin panel (event-card / event-page chip),
 * so the two surfaces can never disagree on the state.
 *
 * Client spec (2026-08-01 questionnaire, simplified per Zohar's correction:
 * the calculation is purely CUMULATIVE — how many photos for how much time
 * has passed — no rolling windows):
 * - Unit: PHOTOS (garbage excluded — the count already excludes it).
 * - rate = photos / elapsed hours, measured from the SCHEDULED start.
 * - First 30 minutes: no pace indication at all (warmup).
 * - green ≥ 50/hr · orange < 50/hr.
 * - RED: after 45 elapsed minutes, when the rate is below 40/hr.
 * - TOO FAST: above 90/hr (the client's "more than 45 photos per half
 *   hour") — its own attention alert, independent of the color.
 *
 * Inputs come from /api/minime/photo-counts: photos (admin-exact live count)
 * and elapsedMinutes (server-computed, capped at the scheduled end; null when
 * the schedule is unknown or the event is lite → everything stays silent).
 */

export const PACE = {
  TARGET_PER_HOUR: 60,
  GREEN_MIN_PER_HOUR: 50,
  RED_MAX_PER_HOUR: 40,
  RED_MIN_ELAPSED_MIN: 45,
  FAST_MIN_PER_HOUR: 90, // "מעל 45 תמונות בחצי שעה"
  WARMUP_MIN: 30,
};

/**
 * -> { state: 'silent'|'warmup'|'green'|'orange'|'red', fast: bool,
 *      ratePerHour: number|null }
 * 'silent' = no schedule data (or lite); 'warmup' = first 30 minutes.
 */
export const computePace = ({ photos, elapsedMinutes }) => {
  if (elapsedMinutes == null || typeof photos !== "number") {
    return { state: "silent", fast: false, ratePerHour: null };
  }
  if (elapsedMinutes < PACE.WARMUP_MIN) {
    return { state: "warmup", fast: false, ratePerHour: null };
  }
  // Bands compare the RAW rate — rounding first let 49.5-49.99/hr display
  // green while the spec floor is ≥50 (review finding 2026-08-01). The
  // rounded value is for display only.
  const rawRate = photos / (elapsedMinutes / 60);
  const fast = rawRate > PACE.FAST_MIN_PER_HOUR;
  let state;
  if (elapsedMinutes >= PACE.RED_MIN_ELAPSED_MIN && rawRate < PACE.RED_MAX_PER_HOUR) {
    state = "red";
  } else if (rawRate < PACE.GREEN_MIN_PER_HOUR) {
    state = "orange";
  } else {
    state = "green";
  }
  return { state, fast, ratePerHour: Math.round(rawRate) };
};

/** The client's escalation protocol, shown with the red alert. */
export const RED_PROTOCOL_STEPS = [
  "לקרוא לאורחים להצטלם",
  "לעדכן את מנהל האירוע",
  "להתחיל הגשה חמה",
  "עדכון נוסף למנהל ולשקול מעבר למיקום טוב יותר",
];

/** Short Hebrew alert line for the slow-side STATE (null = no alert).
 * The too-fast note is deliberately separate (paceFastText) — the client
 * specified it as its own alert. */
export const paceAlertText = (pace) => {
  if (!pace) return null;
  if (pace.state === "red") {
    if (pace.ratePerHour === 0) {
      return "טרם צולמו תמונות — נדרשת יוזמה";
    }
    return `קצב נמוך — ${pace.ratePerHour}/שעה (יעד ${PACE.TARGET_PER_HOUR}) — נדרשת יוזמה`;
  }
  if (pace.state === "orange") {
    return `הקצב מעט נמוך (${pace.ratePerHour}/שעה, יעד ${PACE.TARGET_PER_HOUR}) — שימו לב`;
  }
  return null;
};

/** The independent too-fast attention line (null when not fast). */
export const paceFastText = (pace) =>
  pace && pace.fast
    ? `קצב מהיר מאוד (${pace.ratePerHour}/שעה) — מעל 45 תמונות בחצי שעה, שימו לב`
    : null;
